import React, { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Rating,
  Fade,
  Slide,
  Zoom,
} from '@mui/material';
import TvIcon from '@mui/icons-material/Tv';
import SettingsIcon from '@mui/icons-material/Settings';
import SupportIcon from '@mui/icons-material/Support';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';

const Home = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Removed redirection logic
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px"
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      title: 'TV Installation',
      description: 'Professional mounting and setup of your television',
      icon: <TvIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
    {
      title: 'Smart TV Setup & Activate',
      description: 'Configure your smart TV features and streaming services',
      icon: <SettingsIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
    {
      title: '24/7 Support',
      description: 'Round-the-clock technical support for your TV needs',
      icon: <SupportIcon sx={{ fontSize: 60, color: 'primary.main' }} />,
    },
  ];

  const testimonials = [
    {
      name: 'John Anderson',
      role: 'Home Theater Enthusiast',
      comment: 'The team did an amazing job mounting my 75" TV and setting up my entire home theater system. Very professional and knowledgeable!',
      rating: 5,
    },
    {
      name: 'Sarah Williams',
      role: 'Smart Home Owner',
      comment: 'They helped integrate my TV with all my smart home devices. The service was quick and the results are fantastic!',
      rating: 5,
    },
    {
      name: 'Mike Thompson',
      role: 'Business Owner',
      comment: 'We had them install TVs in our office space. Their work was clean, professional, and completed ahead of schedule.',
      rating: 5,
    },
  ];

  const stats = [
    { number: '5000+', label: 'Installations Completed' },
    { number: '98%', label: 'Customer Satisfaction' },
    { number: '15+', label: 'Years Experience' },
    { number: '24/7', label: 'Support Available' },
  ];

  const whyChooseUs = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Licensed & Insured',
      description: 'Fully certified technicians with comprehensive insurance coverage',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Quick Service',
      description: 'Same-day service available for most installations',
    },
    {
      icon: <ThumbUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Satisfaction Guaranteed',
      description: "We're not happy unless you're completely satisfied",
    },
    {
      icon: <PriceCheckIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Competitive Pricing',
      description: 'Best-in-class service at reasonable rates',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
          color: 'white',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Container sx={{ py: { xs: 8, md: 12 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box sx={{ pr: { md: 8 } }}>
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 700,
                      mb: 2,
                      lineHeight: 1.2,
                      color: 'white'
                    }}
                  >
                    Transform Your TV Experience
                  </Typography>
                  <Typography
                    variant="h5"
                    component="p"
                    sx={{
                      mb: 4,
                      opacity: 0.9,
                      lineHeight: 1.6,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                    }}
                  >
                    Professional installation and smart setup services to bring cinema-quality entertainment to your home
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="contained"
                      color="secondary"
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                      }}
                    >
                      Get Started
                    </Button>
                    <Button
                      component={RouterLink}
                      to="/services"
                      variant="outlined"
                      size="large"
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      View Services
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    textAlign: 'center',
                    mt: { xs: 4, md: 0 },
                    transform: 'translateY(20px)',
                    '&:hover': {
                      transform: 'translateY(10px)',
                    },
                    transition: 'transform 0.5s ease-in-out',
                  }}
                >
                  <img
                    src="https://i.ytimg.com/vi/jEAzF6uC4OI/maxresdefault.jpg"
                    alt="Modern TV Setup"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '20px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    }}
                  />
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container>
          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Zoom in timeout={1000 + index * 200} key={index}>
                <Grid item xs={6} md={3}>
                  <Card sx={{ textAlign: 'center', height: '100%', p: 2 }}>
                    <Typography variant="h3" color="primary" gutterBottom>
                      {stat.number}
                    </Typography>
                    <Typography variant="h6">{stat.label}</Typography>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container sx={{ py: 8 }}>
        <Fade in timeout={1000}>
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
            Our Services
          </Typography>
        </Fade>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature, index) => (
            <Slide
              direction="up"
              in
              timeout={1000 + index * 200}
              key={index}
            >
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 2,
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                  }}
                  elevation={2}
                >
                  <Box sx={{ my: 2 }}>{feature.icon}</Box>
                  <CardContent>
                    <Typography variant="h5" component="h3" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Slide>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
            Why Choose Us
          </Typography>
          <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Experience the difference with our professional TV setup services
          </Typography>
          <Grid container spacing={4}>
            {whyChooseUs.map((item, index) => (
              <Fade in timeout={1000 + index * 200} key={index}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 8,
                      },
                    }}
                  >
                    <Box sx={{ mb: 2 }}>{item.icon}</Box>
                    <Typography variant="h6" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Card>
                </Grid>
              </Fade>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
            What Our Clients Say
          </Typography>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Rating value={testimonial.rating} readOnly sx={{ mb: 2 }} />
                    <Typography variant="body1" paragraph sx={{ fontStyle: 'italic' }}>
                      "{testimonial.comment}"
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Enhanced CTA Section */}
      <Box sx={{ py: 8 }}>
        <Container>
          <Card sx={{ bgcolor: 'primary.main', color: 'white', p: { xs: 4, md: 6 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h3" gutterBottom>
                  Ready to Transform Your TV Experience?
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                  Get professional installation and setup services starting from $99
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                  }}
                >
                  Schedule Now
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;